import { Injectable, OnDestroy } from '@angular/core';
import { CanDeactivate } from '@angular/router';

import { of, Observable, Subscription } from 'rxjs';

import { MonitoringSearchComponent } from '../../Shared/components/MonitoringSearch/monitoring-search.component';
import { MonitoringRoutingService } from '../../Core/services/monitoring-routing.service';
import { MonitoringRouteModel } from '../../Shared/models/monitoring-route.model';
import { RouteType } from '../../Shared/enums/route-type.enum';
import { TopicsService } from '../../Core/services/topics.service';

@Injectable()
export class TopicDeactivateGuard implements CanDeactivate<MonitoringSearchComponent>, OnDestroy {

    private currentRoute: MonitoringRouteModel;
    private subscriptions: Subscription[] = [];

    constructor(
        public topicsService: TopicsService,
        public monitoringRoutingService: MonitoringRoutingService
    ) {
        this.subscriptions.push(
            this.monitoringRoutingService.getCurrentRoute().subscribe(
                (currentRoute) => {
                    this.currentRoute = currentRoute;
                }
            )
        );
    }

    canDeactivate(
        component: MonitoringSearchComponent
    ): Observable<boolean> {
        const destinationRoute = this.monitoringRoutingService.getNextRoute();
        const canSkipGuard: boolean = this.canSkipGuard(this.currentRoute, destinationRoute);

        if (canSkipGuard) {
            return of(true);
        }

        return this.topicsService.showExitDialog();
    }

    /**
     * Returns boolean indicating if two routes are siblings
     *
     * @param currentRoute - route to compare
     * @param nextRoute - route to compar
     *
     * @returns boolean
     */
    canSkipGuard(currentRoute: MonitoringRouteModel, nextRoute: MonitoringRouteModel): boolean {
        if (!nextRoute) {
            return false;
        }

        switch (currentRoute.type) {
            case RouteType.topic:
                if (nextRoute.type === RouteType.topicAnalyticsDashboard ||
                    nextRoute.type === RouteType.topicAnalyticsSyndicates) {
                    return currentRoute.value === nextRoute.value;
                }
                return false;
            case RouteType.topicAnalyticsDashboard:
                if (nextRoute.type === RouteType.topic ||
                    nextRoute.type === RouteType.topicAnalyticsSyndicates) {
                    return currentRoute.value === nextRoute.value;
                }
                return false;
            case RouteType.topicAnalyticsSyndicates:
                if (nextRoute.type === RouteType.topic ||
                    nextRoute.type === RouteType.topicAnalyticsDashboard) {
                    return currentRoute.value === nextRoute.value;
                }
                return false;
            case RouteType.adHocAnalyticsDashboard:
                return nextRoute.type === RouteType.updateSearch ||
                nextRoute.type === RouteType.results ||
                nextRoute.type === RouteType.adHocAnalyticsSyndicates;
            case RouteType.adHocAnalyticsSyndicates:
                return nextRoute.type === RouteType.updateSearch ||
                nextRoute.type === RouteType.results ||
                nextRoute.type === RouteType.adHocAnalyticsDashboard;
            case RouteType.updateSearch:
            case RouteType.search:
                return nextRoute.type === RouteType.results ||
                nextRoute.type === RouteType.adHocAnalyticsDashboard ||
                nextRoute.type === RouteType.adHocAnalyticsSyndicates;
            case RouteType.results:
                return nextRoute.type === RouteType.updateSearch ||
                nextRoute.type === RouteType.adHocAnalyticsDashboard ||
                nextRoute.type === RouteType.adHocAnalyticsSyndicates;
        }

        return false;
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
